import { useState, useEffect } from 'react';
import api from '../../services/api';
import { motion } from 'framer-motion';
import { X, User, Calendar, Users, Award, Star } from "lucide-react";

export default function Teams() {
  const [colleges, setColleges] = useState([]);
  const [selectedCollege, setSelectedCollege] = useState(null);

  useEffect(() => {
    const fetchColleges = async () => {
      try {
        const res = await api.get('/colleges');
        setColleges(res.data);
      } catch (err) {
        console.error("Failed to load colleges", err);
      }
    };
    fetchColleges();
  }, []);

  const colorMap = {
    CTE: 'from-blue-400 to-blue-600',
    CBT: 'from-yellow-300 to-yellow-500',
    CLAS: 'from-green-400 to-green-600',
    CCIS: 'from-purple-400 to-purple-600',
    COE: 'from-red-400 to-red-600',
  };

  // 🔹 Hardcoded details for each college
  const collegeDetails = {

    CTE: {
      dean: "TBA",
      established: "TBA",
      students: "TBA",
      points: "TBA",
      gold_medals: "TBA",
      description: "CTE is dedicated to producing future educators with excellence."
    },
    CBT: {
      dean: "TBA",
      established: "TBA",
      students: "TBA",
      points: "TBA",
      gold_medals: "TBA",
      description: "CBT nurtures business leaders and entrepreneurs."
    },
    CLAS: {
      dean: "TBA",
      established: "TBA",
      students: "TBA",
      points: "TBA",
      gold_medals: "TBA",
      description: "CLAS develops future leaders in arts and sciences."
    },
    CCIS: {
      dean: "TBA",
      established: "TBA",
      students: "TBA",
      points: "TBA",
      gold_medals: "TBA",
      description: "CCIS specializes in computer and information sciences."
    },
    COE: {
      dean: "TBA",
      established: "TBA",
      students: "TBA",
      points: "TBA",
      gold_medals: "TBA",
      description: "COE produces world-class engineers and innovators."
    }
  };

  return (
    <section className="mt-12 px-4">
      <header className="mb-8">
        <h2 className="text-3xl font-bold text-center mb-2">Competing Colleges</h2>
        <p className="text-center text-gray-600 max-w-2xl mx-auto">
          Explore the distinguished colleges of SNSU that are competing this season.
        </p>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {colleges.map((college, index) => {
          const details = collegeDetails[college.acronym] || {};
          return (
            <motion.div
              key={college.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.15 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className={`p-6 rounded-lg shadow cursor-pointer bg-gradient-to-br ${colorMap[college.acronym] || 'bg-white'}`}
              onClick={() => setSelectedCollege({ ...college, ...details })}
            >
              <img
                src={college.logo_url || '/placeholder.png'}
                alt={`${college.name} logo`}
                loading="lazy"
                className="w-24 h-24 mx-auto rounded-full mb-4 border-4 border-white shadow-md object-contain bg-white"
              />
              <h3 className="text-2xl font-extrabold text-center text-black">{college.acronym}</h3>
              <p className="text-black text-center text-sm font-medium">{college.name}</p>
              <p className="mt-2 text-black italic text-xs">
                {college.tagline || "Excellence in education and community leadership."}
              </p>
            </motion.div>
          );
        })}
      </div>

      {/* 🔹 Modern Modal */}
      {selectedCollege && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
          >
            {/* Header */}
            <div className={`bg-gradient-to-r ${colorMap[selectedCollege.acronym]} p-6 relative`}>
              <button
                onClick={() => setSelectedCollege(null)}
                className="absolute top-4 right-4 text-white hover:text-gray-200"
              >
                <X size={22} />
              </button>
              <div className="flex flex-col items-center text-center text-white">
                <img
                  src={selectedCollege.logo_url || "/placeholder.png"}
                  alt={selectedCollege.acronym}
                  className="w-20 h-20 rounded-full border-4 border-white shadow-md mb-3 bg-white object-contain"
                />
                <h2 className="text-2xl font-extrabold">{selectedCollege.name}</h2>
                <p className="text-sm opacity-90">{selectedCollege.tagline}</p>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <User className="text-gray-600" size={18} />
                  <p className="text-sm"><span className="font-bold">Dean:</span> {selectedCollege.dean}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="text-gray-600" size={18} />
                  <p className="text-sm"><span className="font-bold">Established:</span> {selectedCollege.established}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <Users className="text-gray-600" size={18} />
                  <p className="text-sm"><span className="font-bold">Students:</span> {selectedCollege.students}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <Star className="text-gray-600" size={18} />
                  <p className="text-sm"><span className="font-bold">Points:</span> {selectedCollege.points}</p>
                </div>
                <div className="flex items-center space-x-2 col-span-2">
                  <Award className="text-gray-600" size={18} />
                  <p className="text-sm"><span className="font-bold">Medals:</span> {selectedCollege.gold_medals}</p>
                </div>
              </div>

              <p className="text-gray-700 text-sm leading-relaxed">
                {selectedCollege.description}
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </section>
  );
}
